import { Button, Card, Table, Input, Typography, Space } from 'antd';
import { useStyles } from '../FulfillmentContextLandingPage';
import { useStoreState, useStoreActions } from '../../store';
import type { ColumnsType } from 'antd/lib/table';
import { useEffect } from 'react';
import { format } from 'date-fns/format';
import type { Simulation } from '../../store/simulation/types';
import { SimulationStatus } from '../../store/simulation/types';
import { ShipiumTag } from '../../components/shared/ShipiumTag';
import {
  ClockCircleOutlined,
  SettingOutlined,
  ExclamationCircleOutlined,
  SearchOutlined,
  CheckCircleOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  HourglassOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom-v5-compat';
import { SimulationActionDropdown } from '../../components/simulation/shared/SimulationActionDropdown';
import logo from '~/assets/symbol.png';
import { SimulationTypeToDisplay } from './utils';
import SimulationOptionsDropdown from './SimulationOptionsDropdown';
import { useSession } from '~/hooks/useSession';
import { useMergedSimulations } from '~/hooks/simulation/useMergedSimulations';

const { Text } = Typography;

const SimulationStatusToTag: Record<string, React.ReactNode> = {
  configuring: (
    <ShipiumTag color="Gold">
      Configuring <ClockCircleOutlined />{' '}
    </ShipiumTag>
  ),
  processing: (
    <ShipiumTag dark={true} color={'Blue'}>
      {' '}
      Processing <ExclamationCircleOutlined />{' '}
    </ShipiumTag>
  ),
  completed: (
    <ShipiumTag dark={true} color={'Teal'}>
      {' '}
      Completed <CheckCircleOutlined />{' '}
    </ShipiumTag>
  ),
};
const ExecutionStatusToTag: Record<string, React.ReactNode> = {
  paused: (
    <ShipiumTag color="Gold">
      Paused <PauseCircleOutlined />{' '}
    </ShipiumTag>
  ),
  active: (
    <ShipiumTag dark={true} color={'Blue'}>
      {' '}
      Active <PlayCircleOutlined />{' '}
    </ShipiumTag>
  ),
  pending: (
    <ShipiumTag dark={true} color={'Grey'}>
      {' '}
      Pending <HourglassOutlined />{' '}
    </ShipiumTag>
  ),
  completed: (
    <ShipiumTag dark={true} color={'Teal'}>
      {' '}
      Completed <PlayCircleOutlined />{' '}
    </ShipiumTag>
  ),
};
export const SIMULATION_ID_SEARCH_PARAM = 'simulation-id';
const SimulationLandingPage = () => {
  const classes = useStyles();
  const { isShipiumView } = useSession();
  const fetchUsers = useStoreActions((actions) => actions.users.fetchUsers);
  const users = useStoreState((state) => state.users.users);
  const { data: simulationSummaries, isLoading: loading } = useMergedSimulations();
  const { sessionPartnerUrl, isOptInEnabled } = useSession();
  const navigate = useNavigate();

  const fullSimulationRights = isOptInEnabled('simulation-opt-in') || isShipiumView;

  useEffect(() => {
    fetchUsers(undefined);
  }, [fetchUsers]);

  const columns: ColumnsType<Simulation> = [
    {
      title: 'Details',
      key: 'details',
      width: 250,
      render: (row: Simulation) => {
        return (
          <div>
            <Button
              type={'link'}
              size={'large'}
              onClick={() => {
                row.simulationStatus === SimulationStatus.CONFIGURING
                  ? navigate(
                      `/${sessionPartnerUrl}/simulation/create/${row.simulationType}?${SIMULATION_ID_SEARCH_PARAM}=${row.simulationId}`,
                    )
                  : navigate(`/${sessionPartnerUrl}/simulation/${row.simulationId}`);
              }}
              style={{
                height: 'auto',
                margin: 0,
                padding: 0,
                fontWeight: 'bold',
              }}
            >
              {row.name}
            </Button>
            <p>{row.description}</p>
          </div>
        );
      },
    },
    {
      title: 'Type',
      key: 'simulationType',
      render: (row: Simulation) => {
        return (
          <Space align="baseline" size={6}>
            <Button
              type={'link'}
              size={'large'}
              onClick={() => {
                row.simulationStatus === SimulationStatus.CONFIGURING
                  ? navigate(
                      `/${sessionPartnerUrl}/simulation/create/${row.simulationType}?${SIMULATION_ID_SEARCH_PARAM}=${row.simulationId}`,
                    )
                  : navigate(`/${sessionPartnerUrl}/simulation/${row.simulationId}`);
              }}
              style={{
                height: 'auto',
                margin: 0,
                padding: 0,
                fontWeight: 'bold',
              }}
            >
              {SimulationTypeToDisplay[row.simulationType]}
            </Button>
            {/* Shipium-only engine badge */}
            {isShipiumView && (
              <Text type="secondary" style={{ fontSize: 14 }}>
                {(row.simulationSetupData?.serviceVersion ?? 'v1').toUpperCase()}
              </Text>
            )}
          </Space>
        );
      },
      width: 200,
    },
    {
      title: 'Created By',
      dataIndex: 'createdBy',
      width: 200,
      render: (value: any, record: Simulation) => {
        const user = users[record.createdBy];
        const userName = user ? `${user.givenName} ${user.surname}` : '(Deactivated User)';
        const hiddenFromPartnerMessage = isShipiumView && !record.visibleToPartner ? 'Hidden from Partner' : '';
        return (
          <div>
            <div>
              {userName}
              <img src={logo} hidden={!isShipiumView} width="20px" alt="Shipium User" style={{ marginLeft: 5 }} />
            </div>
            <div>{format(new Date(record.creationTimestamp), 'PPpp')}</div>
            <div>
              <Text type="warning">{hiddenFromPartnerMessage}</Text>
            </div>
          </div>
        );
      },
    },
    {
      title: 'Simulation Status',
      key: 'simulationStatus',
      render(value) {
        return SimulationStatusToTag[value.simulationStatus] ?? <ShipiumTag>{value.simulationStatus}</ShipiumTag>;
      },
      width: 150,
    },
    {
      title: 'Execution Status',
      key: 'executionStatus',
      render(value) {
        return ExecutionStatusToTag[value.executionStatus] ?? <ShipiumTag>{value.executionStatus}</ShipiumTag>;
      },
      width: 150,
    },
    ...(fullSimulationRights
      ? [
          {
            title: 'Actions',
            key: 'actions',
            width: 100,
            render(value: Simulation) {
              return <SimulationActionDropdown simulation={value} />;
            },
          },
        ]
      : []),
  ];
  return (
    <Card>
      {
        //todo determine if this search is happening in memory or via api
      }
      <Input prefix={<SearchOutlined />} style={{ width: '70%' }} placeholder={'Search simulations'} />

      <div style={{ float: 'right', display: 'flex', gap: 8 }}>
        {fullSimulationRights && (
          <Button
            onClick={() => {
              navigate(`/${sessionPartnerUrl}/simulation/create`);
            }}
            type="primary"
          >
            <SettingOutlined />
            Create a new Simulation
          </Button>
        )}
        <SimulationOptionsDropdown simulationSummaries={simulationSummaries} />
      </div>

      <Table
        loading={loading}
        size={'large'}
        columns={columns}
        dataSource={
          // don't show simulations that are configuring to
          // users that can only view results
          // TODO move this to the api level
          simulationSummaries.filter(
            (val) => fullSimulationRights || val.simulationStatus !== SimulationStatus.CONFIGURING,
          )
        }
        bordered={false}
        pagination={{ defaultPageSize: 50 }}
        className={classes.tableStyles}
      />
    </Card>
  );
};
export default SimulationLandingPage;
